import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Package, LogOut, ArrowUpRight, Coins, Repeat, Award } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { api } from "@/lib/api";

const TIER_STYLES = {
    bronze: "text-[#8a6a3f] bg-[#f2e9dc] border-[#d9c69a]",
    silver: "text-neutral-700 bg-neutral-100 border-neutral-300",
    gold: "text-[#8a6c00] bg-[#fff5d4] border-[#e5c95c]",
    platinum: "text-[#0077b6] bg-[#e6f6fd] border-[#66c9ee]",
};

export default function Account() {
    const { t } = useTranslation();
    const { user, checked, logout } = useAuth();
    const { addItem, setOpen: setCartOpen } = useCart();
    const navigate = useNavigate();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loyalty, setLoyalty] = useState(null);
    const [reorderingId, setReorderingId] = useState(null);

    useEffect(() => {
        if (user && user !== false) {
            api.get("/orders").then((r) => setOrders(r.data)).finally(() => setLoading(false));
            api.get("/loyalty/balance").then((r) => setLoyalty(r.data)).catch(() => {});
        }
    }, [user]);

    const reorder = async (e, orderId) => {
        e.preventDefault();
        e.stopPropagation();
        setReorderingId(orderId);
        try {
            const { data } = await api.post(`/orders/${orderId}/reorder`);
            const items = data.items || [];
            if (items.length === 0) {
                setReorderingId(null);
                return;
            }
            for (const it of items) {
                addItem(
                    {
                        id: it.product_id,
                        slug: it.slug,
                        name: it.name,
                        image: it.image,
                        price: it.price,
                    },
                    it.quantity,
                    it.dose
                );
            }
            setCartOpen(true);
            navigate("/checkout");
        } catch (err) {
            console.error("[Account] reorder failed:", err);
            toast.error(t("auth.reorderError"));
        } finally {
            setReorderingId(null);
        }
    };

    if (!checked) return <div className="max-w-[1400px] mx-auto px-6 py-24 text-neutral-500">{t("common.loading")}</div>;
    if (user === false) return <Navigate to="/login" replace />;

    return (
        <div data-testid="account-page" className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-12 py-10 sm:py-16 lg:py-24">
            <div className="border-b border-neutral-200 pb-8 sm:pb-10 mb-8 sm:mb-10 flex flex-wrap items-end justify-between gap-4 sm:gap-6">
                <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.28em] text-[#0077b6] mb-3">{t("auth.accountEyebrow")}</div>
                    <h1 className="font-display text-3xl sm:text-4xl lg:text-6xl font-extrabold uppercase tracking-tighter break-words">
                        {t("auth.hello", { name: user.name?.split(" ")[0] })}
                    </h1>
                    <p className="text-neutral-600 mt-2 text-sm truncate">{user.email}</p>
                </div>
                <button
                    onClick={logout}
                    className="inline-flex items-center gap-2 border border-neutral-200 hover:border-[#0089c8] hover:text-black rounded-lg px-5 py-3 text-xs uppercase tracking-[0.18em] font-semibold"
                    data-testid="account-logout"
                >
                    <LogOut className="w-4 h-4" /> {t("auth.signOut")}
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/5 mb-8">
                <Stat label={t("auth.ordersCount")} value={orders.length.toString()} />
                <Stat label={t("auth.statStatus")} value={user.role === "admin" ? t("auth.admin") : t("auth.verified")} />
                <Stat label={t("auth.statSince")} value={new Date().getFullYear().toString()} />
            </div>

            {loyalty && (
                <div
                    className="border border-[#00aeef]/30 bg-gradient-to-br from-[#00aeef]/[0.05] to-white rounded-xl p-5 sm:p-6 mb-10 sm:mb-12"
                    data-testid="loyalty-card"
                >
                    <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-full bg-[#0089c8] text-white flex items-center justify-center flex-shrink-0">
                                <Coins className="w-6 h-6" />
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.22em] text-[#0077b6]">{t("loyalty.eyebrow")}</div>
                                <div className="font-display text-2xl sm:text-3xl font-extrabold text-neutral-900 mt-0.5" data-testid="loyalty-points">
                                    {loyalty.points.toLocaleString()} <span className="text-base font-bold text-neutral-500">{t("loyalty.pts")}</span>
                                </div>
                                <div className="text-xs text-neutral-500 mt-0.5">
                                    {t("loyalty.worth", { amount: loyalty.value_gbp.toFixed(2) })}
                                </div>
                            </div>
                        </div>
                        {loyalty.tier && (
                            <div className="flex flex-col items-end">
                                <div
                                    className={`inline-flex items-center gap-1.5 border rounded-lg px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] font-bold ${TIER_STYLES[loyalty.tier.key] || TIER_STYLES.bronze}`}
                                    data-testid="loyalty-tier"
                                >
                                    <Award className="w-3.5 h-3.5" /> {loyalty.tier.label}
                                </div>
                                {loyalty.tier.earn_multiplier > 1 && (
                                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#0077b6] font-semibold mt-1">
                                        {loyalty.tier.earn_multiplier}× {t("loyalty.earnMultiplier")}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {loyalty.next_tier && (
                        <div className="mb-3" data-testid="loyalty-next-tier">
                            <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.22em] text-neutral-500 mb-1.5">
                                <span>{t("loyalty.progressTo", { label: loyalty.next_tier.label })}</span>
                                <span className="text-[#0077b6] font-bold">
                                    {loyalty.next_tier.points_to_next.toLocaleString()} {t("loyalty.pts")}
                                </span>
                            </div>
                            <div className="w-full h-2 bg-neutral-100 rounded-full overflow-hidden">
                                <div
                                    className="h-full bg-[#0089c8] transition-all duration-500"
                                    style={{
                                        width: `${Math.min(100, Math.round((loyalty.lifetime_points / loyalty.next_tier.min_lifetime) * 100))}%`,
                                    }}
                                />
                            </div>
                        </div>
                    )}

                    <div className="text-xs text-neutral-500 leading-relaxed">
                        {t("loyalty.hint", {
                            earn: loyalty.earn_per_gbp,
                            value: (loyalty.point_value_gbp * 100).toFixed(0),
                            pct: Math.round(loyalty.max_redemption_pct * 100),
                        })}
                        {loyalty.tier && loyalty.tier.free_ship_countries && (
                            <span className="block mt-1 text-[#0077b6] font-semibold">
                                {t(`loyalty.perks.${loyalty.tier.key}`)}
                            </span>
                        )}
                    </div>
                </div>
            )}

            <h2 className="font-display text-2xl lg:text-3xl font-bold uppercase tracking-tight mb-6 flex items-center gap-3">
                <Package className="w-5 h-5 text-[#0077b6]" /> {t("auth.orderHistory")}
            </h2>

            {loading ? (
                <div className="text-neutral-500 text-sm">{t("common.loading")}</div>
            ) : orders.length === 0 ? (
                <div className="border border-neutral-200 p-10 text-center" data-testid="no-orders">
                    <div className="text-neutral-500 mb-4">{t("auth.noOrders")}</div>
                    <Link to="/shop" className="inline-block bg-[#0089c8] text-white font-bold uppercase tracking-[0.18em] text-xs px-6 py-3">
                        {t("checkout.browseCatalog")}
                    </Link>
                </div>
            ) : (
                <div className="space-y-px bg-white/5 rounded-xl overflow-hidden">
                    {orders.map((o) => (
                        <div
                            key={o.id}
                            className="bg-white hover:bg-[#f8f8f8] p-4 sm:p-6 lg:p-8 grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-6 gap-3 sm:gap-4 items-center transition-colors group"
                            data-testid={`order-row-${o.id}`}
                        >
                            <Link to={`/order/${o.id}`} className="col-span-2 sm:col-span-1 min-w-0">
                                <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 mb-1">{t("auth.rowReference")}</div>
                                <div className="font-mono text-xs sm:text-sm text-[#0077b6] truncate">{o.id}</div>
                            </Link>
                            <Link to={`/order/${o.id}`}>
                                <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 mb-1">{t("auth.rowDate")}</div>
                                <div className="font-mono text-xs text-neutral-700">{new Date(o.created_at).toLocaleDateString()}</div>
                            </Link>
                            <Link to={`/order/${o.id}`}>
                                <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 mb-1">{t("auth.rowItems")}</div>
                                <div className="text-sm text-neutral-700">{o.items.length}</div>
                            </Link>
                            <Link to={`/order/${o.id}`}>
                                <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 mb-1">{t("auth.rowTotal")}</div>
                                <div className="font-display font-bold text-lg">£{o.total.toFixed(2)}</div>
                            </Link>
                            <button
                                type="button"
                                onClick={(e) => reorder(e, o.id)}
                                disabled={reorderingId === o.id}
                                className="inline-flex items-center justify-center gap-2 text-[10px] uppercase tracking-[0.22em] font-bold text-white bg-[#0089c8] hover:bg-[#0077b6] disabled:opacity-50 px-3 py-2 rounded-lg transition-colors"
                                data-testid={`reorder-${o.id}`}
                            >
                                <Repeat className="w-3 h-3" />
                                {reorderingId === o.id ? t("auth.reordering") : t("auth.reorder")}
                            </button>
                            <Link
                                to={`/order/${o.id}`}
                                className="flex items-center justify-end col-span-1 sm:col-span-1"
                                aria-label={t("auth.viewOrder")}
                            >
                                <ArrowUpRight className="w-5 h-5 text-neutral-500 group-hover:text-black group-hover:rotate-45 transition-all" />
                            </Link>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div className="bg-white p-8">
            <div className="font-display text-4xl font-extrabold text-[#0077b6] ">{value}</div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 mt-2">{label}</div>
        </div>
    );
}
